import React, { useEffect, useState } from "react";
import logo from "../../assets/logo-green.png";
import userplaceholder from "../../assets/user-placeholder.png";
import { Link, useLocation } from "react-router-dom";
import { FiMenu, FiBell } from "react-icons/fi";
import API from "../../utils/API";

function NavBar() {
  const noSidebar = ['/', '/login', '/create/account'];
  const [hidden, setHidden] = useState(false);
  const [currentLocation, setCurrentLocation] = useState();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();

  useEffect(() => {
    let retries = 10;
    const interval = setInterval(() => {
      const storedUserJSON = localStorage.getItem("yc_usr_dt_ls");
      if (storedUserJSON) {
        try {
          const parsedUser = JSON.parse(storedUserJSON);
          setUser(parsedUser);
          clearInterval(interval);
        } catch (error) {
          console.error("Failed to parse yc_usr_dt_ls:", error);
          clearInterval(interval);
        }
      } else {
        retries--;
        if (retries <= 0) clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => setCurrentLocation(location.pathname), [location.pathname]);

  useEffect(() => setHidden(noSidebar.includes(currentLocation)), [currentLocation]);

  // Poll the unread notification count every 30s, plus once on route change.
  useEffect(() => {
    if (!user || hidden) return;
    let alive = true;
    const fetchCount = () => {
      const cancelToken = API.cancelToken();
      API.getNotificationsUnreadCount(cancelToken)
        .then((res) => { if (alive) setUnreadCount(res.data?.unread || 0); })
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => { alive = false; clearInterval(interval); };
  }, [user, hidden, currentLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  const clickLogo = () => {
    if (currentLocation !== "/dashboard") window.location.href = '/dashboard';
  };

  return (
    <>
      {!hidden && (
        <div className="flex justify-between items-center px-6 py-4 shadow-sm bg-neutral-800 relative" onMouseLeave={() => setIsDropdownOpen(false)}>
          {/* Logo */}
          <img
            src={logo}
            className="max-w-[160px] max-h-[28px] cursor-pointer"
            onClick={clickLogo}
            alt="Logo"
          />

          {/* Desktop navigation */}
          <div className="hidden md:flex gap-5 items-center text-base ml-auto text-white whitespace-nowrap">
            {user && ["ADMIN", "RECEPCAO", "BOSS"].includes(user.role) && (
              <NavItem label="Home" path="/dashboard" current={currentLocation} />
            )}

            {/* Agenda */}
            {user && ["ADMIN", "MECANICO", "COMPRADOR", "RECEPCAO", "BOSS"].includes(user.role) && (
              <NavItem label="Agenda" path="/schedule" current={currentLocation} />
            )}

            {/* Serviços */}
            {user && ["ADMIN", "MECANICO", "COMPRADOR", "RECEPCAO", "BOSS"].includes(user.role) && (
              <NavItem label="Serviços" path="/services" current={currentLocation} />
            )}

            {/* Aprovações (somente ADMIN e BOSS) */}
            {user && ["ADMIN", "BOSS"].includes(user.role) && (
              <NavItem label="Aprovações" path="/boss/approvals" current={currentLocation} />
            )}

            {/* Atribuir Mecânico (somente ADMIN e BOSS) */}
            {user && ["ADMIN", "BOSS"].includes(user.role) && (
              <NavItem label="Atribuir Mecânico" path="/pending/assignments" current={currentLocation} />
            )}

            {/* Clientes */}
            {user && ["ADMIN", "RECEPCAO", "BOSS"].includes(user.role) && (
              <NavItem label="Clientes" path="/clients" current={currentLocation} />
            )}

            {/* Estatísticas */}
            {user && ["ADMIN", "BOSS"].includes(user.role) && (
              <NavItem label="Estatísticas" path="/statistics" current={currentLocation} />
            )}
          </div>

          {/* Mobile hamburger */}
          <div className="md:hidden flex items-center text-white">
            <FiMenu size={24} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="cursor-pointer" />
          </div>

          {/* Notification bell */}
          {user && (
            <Link
              to="/notifications"
              className={`relative ml-4 p-2 rounded-full hover:bg-neutral-700 transition-colors ${currentLocation === "/notifications" ? "bg-neutral-700" : ""}`}
              title="Notificações"
            >
              <FiBell size={20} className="text-white" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 min-w-[18px] h-[18px] text-[10px] font-bold leading-none text-white bg-red-600 rounded-full ring-2 ring-neutral-800">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </Link>
          )}

          {/* User dropdown */}
          <div className="flex gap-2 items-center relative ml-4">
            <div
              className="flex items-center cursor-pointer gap-2 text-white"
              onMouseEnter={() => setIsDropdownOpen(true)}
            >
              <img src={user?.company?.logo_url || userplaceholder} className="w-6 h-6 rounded-full" alt="User Avatar" />
              <span className="hidden sm:inline">{user?.company?.fantasy_name || ""}</span>
            </div>

            {isDropdownOpen && (
              <div
                className="absolute top-full right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50"
                onMouseEnter={() => setIsDropdownOpen(true)}
              >
                <div className="py-1">
                  {user && ["ADMIN", "RECEPCAO", "BOSS"].includes(user.role) && (
                    <>
                      <Link to="/manage/checklist" className="block px-4 py-2 text-gray-800 hover:bg-gray-100">Configurações de Checklist</Link>
                      <Link to="/manage/parameters" className="block px-4 py-2 text-gray-800 hover:bg-gray-100">Parâmetros</Link>
                      <Link to="/manage/users" className="block px-4 py-2 text-gray-800 hover:bg-gray-100">Usuários</Link>
                    </>
                  )}
                  <div onClick={handleLogout} className="block px-4 py-2 text-gray-800 hover:bg-gray-100 cursor-pointer">Sair</div>
                </div>
              </div>
            )}
          </div>

          {/* Mobile menu dropdown */}
          {isMobileMenuOpen && (
            <div className="absolute top-full left-0 w-full bg-white text-gray-800 shadow-md z-50 md:hidden">
              <div className="flex flex-col py-2">
                {user && (
                  <Link to="/notifications" className="px-6 py-3 hover:bg-gray-100 border-b border-gray-200 flex items-center justify-between">
                    <span>Notificações</span>
                    {unreadCount > 0 && (
                      <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </Link>
                )}
                {user && ["ADMIN", "RECEPCAO", "BOSS"].includes(user.role) && <MobileNavItem label="Home" path="/dashboard" />}
                {user && ["ADMIN", "MECANICO", "COMPRADOR", "RECEPCAO", "BOSS"].includes(user.role) && (
                  <MobileNavItem label="Agenda" path="/schedule" />
                )}
                {user && ["ADMIN", "MECANICO", "COMPRADOR", "RECEPCAO", "BOSS"].includes(user.role) && (
                  <MobileNavItem label="Serviços" path="/services" />
                )}
                {user && ["ADMIN", "BOSS"].includes(user.role) && (
                  <MobileNavItem label="Aprovações" path="/boss/approvals" />
                )}
                {user && ["ADMIN", "BOSS"].includes(user.role) && (
                  <MobileNavItem label="Atribuir Mecânico" path="/pending/assignments" />
                )}
                {user && ["ADMIN", "RECEPCAO", "BOSS"].includes(user.role) && (
                  <MobileNavItem label="Clientes" path="/clients" />
                )}
                {user && ["ADMIN", "BOSS"].includes(user.role) && (
                  <MobileNavItem label="Estatísticas" path="/statistics" />
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

const NavItem = ({ label, path, current }) => (
  <Link to={path} className={`flex flex-col justify-center px-4 pt-2 ${current === path ? "text-lime-300" : ""}`}>
    <span>{label}</span>
    <div className={`h-0.5 mt-1 ${current === path ? "bg-lime-300" : ""}`} />
  </Link>
);

const MobileNavItem = ({ label, path }) => (
  <Link
    to={path}
    className="px-6 py-3 hover:bg-gray-100 border-b border-gray-200"
  >
    {label}
  </Link>
);

export default NavBar;
